import React from 'react';
import { useTranslate } from '@bynder/localization';
import { Form, Tabs } from '@bynder/design-system';
import { useActiveSection } from '../../../../queries/guides';
import {
  DEFAULT_SECTION_WIDTH,
  SECTION_WIDTH_L,
  SECTION_WIDTH_M,
  SECTION_WIDTH_S,
  SECTION_WIDTH_XL,
} from '../../../../constants/sections';
import { useSectionMutations } from '../../../../mutations/sections';
import { StyledInput } from '../../Styles';

export const SectionWidthEditor = () => {
  const { translate } = useTranslate();
  const { activeSection } = useActiveSection();
  const { editSectionWidth } = useSectionMutations();

  const [sectionWidth, setSectionWidth] = React.useState(
    activeSection?.appearance.width,
  );

  React.useEffect(() => {
    setSectionWidth(activeSection.appearance.width);
  }, [activeSection.appearance.width, activeSection.id]);

  const handleWidthBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    let cleanWidthValue;

    if (event.target.value === '') {
      cleanWidthValue = DEFAULT_SECTION_WIDTH;
    } else {
      cleanWidthValue = setWidthLimits(Number(event.target.value));
    }

    setSectionWidth(cleanWidthValue);

    if (cleanWidthValue !== activeSection.appearance.width) {
      editSectionWidth.mutate({
        sectionId: activeSection.id,
        sectionWidth: cleanWidthValue,
      });
    }
  };

  const handleWidthEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (setWidthLimits(sectionWidth) !== activeSection.appearance.width) {
        setSectionWidth(setWidthLimits(sectionWidth));
        editSectionWidth.mutate({
          sectionId: activeSection.id,
          sectionWidth: setWidthLimits(sectionWidth),
        });
      }
    }
  };

  const handleChangeWidth = (newWidth: number) => {
    if (setWidthLimits(newWidth) !== activeSection.appearance.width) {
      editSectionWidth.mutate({
        sectionId: activeSection.id,
        sectionWidth: setWidthLimits(newWidth),
      });
    }
  };

  return (
    <Form.Row>
      <Form.Group>
        <StyledInput
          type="number"
          suffix="px"
          prefix="W"
          value={sectionWidth?.toString()}
          onChange={newValue => {
            if (newValue === '') setSectionWidth(0);
            else setSectionWidth(Number(newValue));
          }}
          onKeyDown={event => handleWidthEnter(event)}
          onBlur={handleWidthBlur}
        />
      </Form.Group>
      <Form.Group>
        <Tabs variant="clean">
          <Tabs.Tab
            data-testid="section-width-small"
            label={translate('SECTION_INSPECTOR.SECTION_WIDTH_SMALL')}
            isActive={activeSection.appearance.width === SECTION_WIDTH_S}
            onClick={() => handleChangeWidth(SECTION_WIDTH_S)}
          />
          <Tabs.Tab
            data-testid="section-width-medium"
            label={translate('SECTION_INSPECTOR.SECTION_WIDTH_MEDIUM')}
            isActive={activeSection.appearance.width === SECTION_WIDTH_M}
            onClick={() => handleChangeWidth(SECTION_WIDTH_M)}
          />
          <Tabs.Tab
            data-testid="section-width-large"
            label={translate('SECTION_INSPECTOR.SECTION_WIDTH_LARGE')}
            isActive={activeSection.appearance.width === SECTION_WIDTH_L}
            onClick={() => handleChangeWidth(SECTION_WIDTH_L)}
          />
        </Tabs>
      </Form.Group>
    </Form.Row>
  );
};

const setWidthLimits = (w: number) => {
  if (w < SECTION_WIDTH_S) return SECTION_WIDTH_S;

  if (w > SECTION_WIDTH_XL) return SECTION_WIDTH_XL;

  return w;
};
